/**
 * MKS-410 — how a request record reads on screen.
 *
 * Kept out of the components, like MKS-339's `slaFormat.ts`, so the rules a
 * reader has to trust (which word a status shows as, which colour it carries)
 * are testable without rendering anything — and so the queue, "My requests"
 * and the dashboard card cannot disagree about what `in_progress` is called.
 *
 * Tones are inline-style colour pairs, not Tailwind classes: `Pill` takes
 * `bg`/`fg` as data-as-style (Phase 41 PATTERNS.md), and these mirror
 * `STATUS_TONES` in `slaFormat.ts`.
 */
import type {
  HelpdeskRequestRecord,
  HelpdeskRequestStatus,
} from '../../services/api/helpdeskRequests';

export interface RequestTone {
  label: string;
  bg: string;
  fg: string;
}

/**
 * The five statuses. `new` is amber because an unrouted request is the one
 * thing the triage queue exists to clear; `rejected` is slate rather than red
 * because rejecting is a legitimate outcome, not a failure.
 */
export const REQUEST_STATUS_TONES: Record<HelpdeskRequestStatus, RequestTone> = {
  new: { label: 'New', bg: '#fef3c7', fg: '#92400e' },
  assigned: { label: 'Assigned', bg: '#dbeafe', fg: '#1e40af' },
  in_progress: { label: 'In progress', bg: '#e0e7ff', fg: '#3730a3' },
  resolved: { label: 'Resolved', bg: '#dcfce7', fg: '#166534' },
  rejected: { label: 'Rejected', bg: '#e2e8f0', fg: '#334155' },
};

/** A status that is not one of the five (a hand-edited row) still reads. */
export function requestStatusTone(status: string): RequestTone {
  return (
    REQUEST_STATUS_TONES[status as HelpdeskRequestStatus] ?? {
      label: status,
      bg: '#e2e8f0',
      fg: '#334155',
    }
  );
}

export const REQUEST_SOURCE_TONES: Record<string, RequestTone> = {
  internal: { label: 'Internal', bg: '#ede9fe', fg: '#5b21b6' },
  client: { label: 'Client', bg: '#ccfbf1', fg: '#115e59' },
};

export function requestSourceTone(source: string): RequestTone {
  return (
    REQUEST_SOURCE_TONES[source] ?? { label: source, bg: '#e2e8f0', fg: '#334155' }
  );
}

export const REQUEST_PRIORITY_LABELS: Record<string, string> = {
  urgent: 'Urgent',
  high: 'High',
  normal: 'Normal',
  low: 'Low',
};

export function requestPriorityLabel(priority: string): string {
  return REQUEST_PRIORITY_LABELS[priority] ?? priority;
}

/**
 * Where a request stands, for the reporter: "Nobody has picked this up yet"
 * beats showing them the word `new` and letting them guess.
 */
export function reporterStatusHint(request: HelpdeskRequestRecord): string {
  switch (request.status) {
    case 'new':
      return 'Waiting for triage';
    case 'assigned':
      return request.assignee
        ? `Picked up by ${request.assignee.name}`
        : 'Routed, waiting to be picked up';
    case 'in_progress':
      return 'Being worked on';
    case 'resolved':
      return 'Resolved';
    case 'rejected':
      return 'Rejected';
    default:
      return request.status;
  }
}

/**
 * The closing text, whichever of the two it is. One accessor so no caller has
 * to remember that a resolved request carries a note and a rejected one a
 * reason.
 */
export function requestOutcomeText(
  request: HelpdeskRequestRecord,
): { label: string; text: string } | null {
  if (request.status === 'resolved' && request.resolutionNote) {
    return { label: 'Resolution', text: request.resolutionNote };
  }
  if (request.status === 'rejected' && request.rejectionReason) {
    return { label: 'Reason for rejection', text: request.rejectionReason };
  }
  return null;
}

/** Where a client request came from, as one string for the Source column. */
export function requestSourceDetail(request: HelpdeskRequestRecord): string | null {
  if (request.source !== 'client') return null;
  return request.projectName ?? null;
}
